// Login, signup, and verify — one file, three small screens, shared shell.
import type { ReactNode } from "react";
import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../api";
import { setSession } from "../session";
import { go } from "../router";
import { BrandLockup } from "../components/KeeperMark";
import { opinlyAnonId, opinlyIdentify, opinlyTrack, signupEventId } from "../lib/opinly";
import { identify, track } from "../lib/analytics";
import { metaBrowserContext, type MetaBrowserContext } from "../lib/meta";
import { API_BASE } from "../lib/apiBase";

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="auth-wrap">
      <div style={{ display: "flex", flexDirection: "column", gap: 18, width: "100%", maxWidth: 380 }}>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <BrandLockup />
        </div>
        <div className="auth-card">{children}</div>
      </div>
    </div>
  );
}

function afterLogin(): void {
  const returnTo = sessionStorage.getItem("fk_return_to");
  sessionStorage.removeItem("fk_return_to");
  if (returnTo && !returnTo.includes("login") && !returnTo.includes("signup") && !returnTo.includes("auth/google")) {
    location.hash = returnTo;
  } else {
    go({ name: "product", slug: null, section: "dashboard" });
  }
}

function googleStartUrl(anonId?: string, meta?: MetaBrowserContext): string {
  const url = new URL(`${API_BASE}/auth/google/start`);
  if (anonId) url.searchParams.set("anonId", anonId);
  if (meta) {
    url.searchParams.set("meta_event_id", meta.event_id);
    if (meta.fbp) url.searchParams.set("meta_fbp", meta.fbp);
    if (meta.fbc) url.searchParams.set("meta_fbc", meta.fbc);
    if (meta.source_url) url.searchParams.set("meta_source_url", meta.source_url.slice(0, 2048));
  }
  return url.toString();
}

function GoogleButton({ label, surface }: { label: string; surface: "login" | "signup" }) {
  return (
    <a
      className="btn-google"
      href={googleStartUrl()}
      onClick={(e) => {
        track("google_auth_start", { method: "google", surface });
        const anonId = opinlyAnonId();
        const meta = surface === "signup" ? metaBrowserContext() : undefined;
        if (!anonId && !meta) return;
        e.preventDefault();
        location.assign(googleStartUrl(anonId, meta));
      }}
    >
      <GoogleMark />
      {label}
    </a>
  );
}

function OrRule() {
  return <div className="auth-or" role="separator">or</div>;
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

const LOCAL_DEV = location.hostname === "localhost" || location.hostname === "127.0.0.1";

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const justVerified = location.hash.includes("verified=1");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.auth.login(email, password);
      setSession({ token: res.token, account: res.account });
      opinlyIdentify({ email: res.account.email, userId: res.account.id });
      identify({ userId: res.account.id, email: res.account.email });
      opinlyTrack("login", { method: "email" });
      track("login", { method: "email" });
      afterLogin();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <h1 style={{ fontSize: 17 }}>Sign in</h1>
      {justVerified && <div className="form-note" style={{ color: "var(--pos)" }}>Email verified. Sign in to continue.</div>}
      <GoogleButton label="Continue with Google" surface="login" />
      <OrRule />
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="field">
          <label htmlFor="email">Email</label>
          <input id="email" className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
        </div>
        <div className="field">
          <label htmlFor="password">Password{LOCAL_DEV ? " (optional locally)" : ""}</label>
          <input id="password" className="input" type="password" required={!LOCAL_DEV} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
        </div>
        {LOCAL_DEV && (
          <div className="form-note">
            Local API — existing accounts can sign in with email only. Google login still needs the production redirect.
          </div>
        )}
        {error && <div className="form-error">{error}</div>}
        <button type="submit" className="btn-solid" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
      </form>
      <div className="form-note">
        No account? <a href="#/signup">Create one</a>.
      </div>
    </Shell>
  );
}

export function Signup() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) return setError("Passwords don't match.");
    if (password.length < 10) return setError("Password needs at least 10 characters.");
    setBusy(true);
    setError(null);
    try {
      const meta = metaBrowserContext();
      await api.auth.signup(email, password, opinlyAnonId(), meta);
      opinlyIdentify({ email });
      opinlyTrack("sign_up", { method: "email" }, { externalEventId: signupEventId(email) });
      track("sign_up", { method: "email", event_id: meta.event_id, email, dedupe_id: signupEventId(email) });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <Shell>
        <h1 style={{ fontSize: 17 }}>Check your email</h1>
        <div className="form-note">
          A confirmation link is on its way to <strong>{email}</strong>. The link signs you in.
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 style={{ fontSize: 17 }}>Create your account</h1>
      <GoogleButton label="Continue with Google" surface="signup" />
      <OrRule />
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="field">
          <label htmlFor="s-email">Email</label>
          <input id="s-email" className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
        </div>
        <div className="field">
          <label htmlFor="s-password">Password (10+ characters)</label>
          <input id="s-password" className="input" type="password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        </div>
        <div className="field">
          <label htmlFor="s-confirm">Confirm password</label>
          <input id="s-confirm" className="input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
        </div>
        {error && <div className="form-error">{error}</div>}
        <button type="submit" className="btn-solid" disabled={busy}>{busy ? "Creating…" : "Create account"}</button>
      </form>
      <div className="form-note">
        Already registered? <a href="#/login">Sign in</a>.
      </div>
      <div className="form-note">
        By creating an account you agree to the{" "}
        <a href="https://funnelkeeper.com/terms/" target="_blank" rel="noreferrer">terms</a>
        {" "}and{" "}
        <a href="https://funnelkeeper.com/privacy/" target="_blank" rel="noreferrer">privacy policy</a>.
      </div>
    </Shell>
  );
}

/** An invite or owner-claim link (#/invite/<token>): new people set a password;
 *  existing logins just join. The single-use token is the credential. */
export function AcceptInvite({ token }: { token: string }) {
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [peek, setPeek] = useState<{ email: string; account_name: string; needs_password: boolean } | null>(null);
  const [peekError, setPeekError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.auth.invitePeek(token).then(
      (info) => { if (!cancelled) setPeek(info); },
      (err) => { if (!cancelled) setPeekError(err instanceof ApiError ? err.message : String(err)); },
    );
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const needsPassword = peek?.needs_password !== false;
    if (needsPassword) {
      if (password !== confirm) return setError("Passwords don't match.");
      if (password.length < 10) return setError("Password needs at least 10 characters.");
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.auth.acceptInvite(
        token,
        needsPassword ? password : undefined,
        name.trim() || undefined,
      );
      setSession({ token: res.token, account: res.account });
      opinlyIdentify({ email: res.account.email, userId: res.account.id });
      identify({ userId: res.account.id, email: res.account.email });
      track("invite_accepted");
      go({ name: "product", slug: null, section: "dashboard" });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (peekError) {
    return (
      <Shell>
        <h1 style={{ fontSize: 17 }}>Invite expired</h1>
        <div className="form-note">{peekError} Ask the owner for a fresh link.</div>
      </Shell>
    );
  }

  if (!peek) {
    return (
      <Shell>
        <h1 style={{ fontSize: 17 }}>Opening invite…</h1>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 style={{ fontSize: 17 }}>{peek.needs_password ? "Set your password" : "Join this workspace"}</h1>
      <div className="form-note">
        {peek.needs_password
          ? <>Choose a password to join <strong>{peek.account_name}</strong>.</>
          : <>Accept the invite to add <strong>{peek.account_name}</strong> to your FunnelKeeper login ({peek.email}).</>}
      </div>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {peek.needs_password && (
          <div className="field">
            <label htmlFor="i-name">Your name (optional)</label>
            <input id="i-name" className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} autoComplete="name" />
          </div>
        )}
        {peek.needs_password && (
          <>
            <div className="field">
              <label htmlFor="i-password">Password (10+ characters)</label>
              <input id="i-password" className="input" type="password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
            </div>
            <div className="field">
              <label htmlFor="i-confirm">Confirm password</label>
              <input id="i-confirm" className="input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
            </div>
          </>
        )}
        {error && <div className="form-error">{error}</div>}
        <button type="submit" className="btn-solid" disabled={busy}>
          {busy ? "Saving…" : peek.needs_password ? "Continue" : "Accept invite"}
        </button>
      </form>
    </Shell>
  );
}

/** A reset link (#/reset/<token>): choose a new password, then sign in. */
export function ResetPassword({ token }: { token: string }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) return setError("Passwords don't match.");
    if (password.length < 10) return setError("Password needs at least 10 characters.");
    setBusy(true);
    setError(null);
    try {
      await api.auth.resetConfirm(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Shell>
        <h1 style={{ fontSize: 17 }}>Password changed</h1>
        <div className="form-note">
          Every signed-in session was signed out. <a href="#/login">Sign in</a> with the new password.
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 style={{ fontSize: 17 }}>Choose a new password</h1>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="field">
          <label htmlFor="r-password">New password (10+ characters)</label>
          <input id="r-password" className="input" type="password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        </div>
        <div className="field">
          <label htmlFor="r-confirm">Confirm password</label>
          <input id="r-confirm" className="input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
        </div>
        {error && <div className="form-error">{error}</div>}
        <button type="submit" className="btn-solid" disabled={busy}>{busy ? "Saving…" : "Set password"}</button>
      </form>
    </Shell>
  );
}

/** Lands on #/auth/google/<ticket> after Google consent. The ticket is the credential. */
export function GoogleCallback({ token }: { token: string }) {
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.auth.googleComplete(token);
        if (cancelled) return;
        setSession({ token: res.token, account: res.account });
        if (res.account?.email) {
          opinlyIdentify({ email: res.account.email, userId: res.account.id });
          identify({ userId: res.account.id, email: res.account.email });
          if (res.is_new) {
            opinlyTrack("sign_up", { method: "google" }, { externalEventId: signupEventId(res.account.email) });
            track("sign_up", { method: "google", email: res.account.email, dedupe_id: signupEventId(res.account.email) });
          } else {
            opinlyTrack("login", { method: "google" });
            track("login", { method: "google" });
          }
        }
        afterLogin();
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : String(err));
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <Shell>
      <h1 style={{ fontSize: 17 }}>{error ? "Sign-in didn’t finish" : "Signing in…"}</h1>
      <div className="form-note">
        {error
          ? <>{error} <a href="#/login">Try again</a>.</>
          : "Finishing Google sign-in."}
      </div>
    </Shell>
  );
}

/** The email link lands on the API (which verifies + redirects to #/login).
 *  This screen handles the DASHBOARD-side variant `#/verify/<token>` for
 *  clients that rewrite links; it simply forwards to the API. */
export function Verify({ token }: { token: string }) {
  const [state, setState] = useState<"working" | "failed">("working");
  useEffect(() => {
    location.href = `${API_BASE}/auth/verify?token=${encodeURIComponent(token)}`;
    const timer = setTimeout(() => setState("failed"), 8000);
    return () => clearTimeout(timer);
  }, [token]);
  return (
    <Shell>
      <h1 style={{ fontSize: 17 }}>{state === "working" ? "Verifying…" : "That took too long"}</h1>
      <div className="form-note">
        {state === "working"
          ? "Confirming your email with the server."
          : "The link may have expired. Sign up again to get a fresh one."}
      </div>
    </Shell>
  );
}
